<template>
  <div class="page-wrapper">
    <HeaderComponent />
    <div class="page-content">
      <AppBreadcrumb :items="[{ label: 'Bosh sahifa', to: '/' }, { label: 'Flesh Card' }]" />

      <!-- Toolbar -->
      <div class="flash-toolbar">
        <div class="toolbar-left">
          <h2 class="flash-title">🧠 Flesh Card</h2>
          <p class="flash-subtitle">Savollarni kartochkalar orqali o'rganing</p>
        </div>
        <div class="toolbar-right">
          <select v-model="selectedTopicId" @change="loadCards" class="topic-select">
            <option :value="null">Barcha mavzular</option>
            <option v-for="t in topics" :key="t.id" :value="t.id">{{ t.name }} ({{ t.question_count }})</option>
          </select>
          <button @click="loadCards" class="refresh-btn" :disabled="loading">🔄</button>
        </div>
      </div>

      <div v-if="loading" class="loading-box"><a-spin size="large" /><p>Kartochkalar yuklanmoqda...</p></div>

      <div v-else-if="cards.length === 0" class="empty-state">
        <div class="empty-icon">📭</div>
        <h3>Kartochkalar topilmadi</h3>
        <p>Boshqa mavzuni tanlang</p>
      </div>

      <!-- Dual Stack Layout -->
      <div v-else class="flash-layout">
        <!-- Left Stack: Remaining -->
        <div class="stack-column stack-left">
          <div class="stack-header">
            <span class="stack-label">📋 Qolgan</span>
            <span class="stack-count">{{ remainingCards.length }}</span>
          </div>
          <div class="mini-stack">
            <div v-for="(c, i) in remainingCards.slice(0, 5)" :key="c.id" class="mini-card" :style="{ transform: `translateY(${i * 4}px) scale(${1 - i * 0.02})`, zIndex: 5 - i, opacity: 1 - i * 0.15 }">
              <p>{{ truncate(c.text, 50) }}</p>
            </div>
            <div v-if="remainingCards.length > 5" class="mini-more">+{{ remainingCards.length - 5 }} ta</div>
          </div>
        </div>

        <!-- Center: Active Card -->
        <div class="stack-column stack-center">
          <!-- Progress -->
          <div class="progress-row">
            <div class="progress-track">
              <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
            </div>
            <span class="progress-num">{{ currentIndex + 1 }}/{{ cards.length }}</span>
          </div>

          <!-- Flashcard -->
          <div class="flashcard-wrap" @click="flipCard">
            <div class="flashcard" :class="{ flipped: isFlipped }">
              <!-- Front -->
              <div class="fc-face fc-front">
                <div class="fc-badge fc-badge-q">SAVOL</div>
                <div v-if="currentCard.image_url" class="fc-img-wrap">
                  <img :src="currentCard.image_url" :alt="currentCard.text" class="fc-img" />
                </div>
                <p class="fc-text">{{ currentCard.text }}</p>
                <div class="fc-hint"><span>👆</span> Javobni ko'rish</div>
              </div>
              <!-- Back -->
              <div class="fc-face fc-back">
                <div class="fc-badge fc-badge-a">JAVOB</div>
                <div class="fc-answer-box">
                  <span class="fc-check">✅</span>
                  <p>{{ currentCard.correct_answer?.text || 'Javob mavjud emas' }}</p>
                </div>
                <div v-if="currentCard.explanation" class="fc-explain">
                  <div class="fc-explain-title">💡 Tushuntirish</div>
                  <p>{{ currentCard.explanation }}</p>
                </div>
                <div class="fc-hint"><span>👆</span> Savolga qaytish</div>
              </div>
            </div>
          </div>

          <!-- Navigation -->
          <div class="fc-nav">
            <button class="nav-btn" @click.stop="prevCard" :disabled="currentIndex === 0">← Oldingi</button>
            <div class="rate-btns">
              <button class="rate-btn r-hard" @click.stop="rateCard('hard')" title="Qiyin">😓</button>
              <button class="rate-btn r-ok" @click.stop="rateCard('ok')" title="Normal">😐</button>
              <button class="rate-btn r-easy" @click.stop="rateCard('easy')" title="Oson">😊</button>
            </div>
            <button class="nav-btn" @click.stop="nextCard" :disabled="currentIndex === cards.length - 1">Keyingi →</button>
          </div>
        </div>

        <!-- Right Stack: Completed -->
        <div class="stack-column stack-right">
          <div class="stack-header">
            <span class="stack-label">✅ Ishlangan</span>
            <span class="stack-count">{{ completedCards.length }}</span>
          </div>
          <div class="mini-stack">
            <div v-for="(c, i) in completedCards.slice(-5).reverse()" :key="c.id" class="mini-card" :class="'mc-' + (ratings[c.id] || 'ok')" :style="{ transform: `translateY(${i * 4}px) scale(${1 - i * 0.02})`, zIndex: 5 - i, opacity: 1 - i * 0.15 }">
              <p>{{ truncate(c.text, 50) }}</p>
              <span class="mc-emoji">{{ ratingEmoji(ratings[c.id]) }}</span>
            </div>
            <div v-if="completedCards.length > 5" class="mini-more">+{{ completedCards.length - 5 }} ta</div>
          </div>

          <!-- Stats -->
          <div v-if="Object.keys(ratings).length" class="rate-stats">
            <div class="rs-item rs-hard"><span>😓</span>{{ ratingStats.hard }}</div>
            <div class="rs-item rs-ok"><span>😐</span>{{ ratingStats.ok }}</div>
            <div class="rs-item rs-easy"><span>😊</span>{{ ratingStats.easy }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HeaderComponent from '../components/HeaderComponent.vue'
import AppBreadcrumb from '../components/AppBreadcrumb.vue'
import api from '@/api/axios'

export default {
  name: 'FlashCardView',
  components: { HeaderComponent, AppBreadcrumb },
  data() {
    return { topics: [], selectedTopicId: null, cards: [], currentIndex: 0, isFlipped: false, loading: false, ratings: {} }
  },
  computed: {
    currentCard() { return this.cards[this.currentIndex] || {} },
    progressPercent() { return this.cards.length ? ((this.currentIndex + 1) / this.cards.length) * 100 : 0 },
    remainingCards() { return this.cards.slice(this.currentIndex + 1) },
    completedCards() { return this.cards.slice(0, this.currentIndex) },
    ratingStats() {
      const v = Object.values(this.ratings)
      return { hard: v.filter(x => x === 'hard').length, ok: v.filter(x => x === 'ok').length, easy: v.filter(x => x === 'easy').length }
    }
  },
  async mounted() {
    try { const r = await api.get('/v1/topics/'); this.topics = r.data?.topics || [] } catch(e) {}
    await this.loadCards()
  },
  methods: {
    async loadCards() {
      this.loading = true; this.currentIndex = 0; this.isFlipped = false; this.ratings = {}
      try {
        const p = { count: 30 }; if (this.selectedTopicId) p.topic_id = this.selectedTopicId
        const r = await api.get('/v1/flashcards/', { params: p }); this.cards = r.data?.cards || []
      } catch(e) { this.cards = [] }
      finally { this.loading = false }
    },
    flipCard() { this.isFlipped = !this.isFlipped },
    nextCard() { if (this.currentIndex < this.cards.length - 1) { this.isFlipped = false; setTimeout(() => this.currentIndex++, 120) } },
    prevCard() { if (this.currentIndex > 0) { this.isFlipped = false; setTimeout(() => this.currentIndex--, 120) } },
    rateCard(r) { if (this.currentCard?.id) this.ratings[this.currentCard.id] = r; this.nextCard() },
    truncate(s, n) { return s && s.length > n ? s.slice(0, n) + '...' : s },
    ratingEmoji(r) { return r === 'hard' ? '😓' : r === 'easy' ? '😊' : '😐' }
  }
}
</script>

<style scoped>
.page-wrapper { min-height:100vh; display:flex; flex-direction:column; background:var(--bg) }
.page-content { flex:1; padding:0 24px 32px; max-width:1300px; margin:0 auto; width:100% }

/* Toolbar */
.flash-toolbar { margin-top:16px; padding:20px 24px; background:var(--bg-card,#11243d); border:1px solid var(--border); border-radius:20px; display:flex; align-items:center; justify-content:space-between; gap:16px; position:relative; overflow:hidden }
.flash-toolbar::before { content:''; position:absolute; top:0; left:0; right:0; height:4px; background:linear-gradient(90deg,#f59e0b,#ef4444,#8b5cf6) }
.toolbar-left { flex:1 }
.flash-title { font-size:20px; font-weight:800; color:var(--text); margin:0 }
.flash-subtitle { font-size:13px; color:var(--text-soft); margin:4px 0 0 }
.toolbar-right { display:flex; gap:8px; align-items:center }
.topic-select { padding:8px 12px; border-radius:10px; border:1px solid var(--border); background:var(--bg); color:var(--text); font-size:13px; min-width:200px }
.refresh-btn { width:36px; height:36px; border-radius:10px; border:1px solid var(--border); background:transparent; font-size:16px; cursor:pointer; transition:all .2s }
.refresh-btn:hover:not(:disabled) { background:rgba(59,130,246,.1); border-color:#3b82f6 }

.loading-box { display:flex; flex-direction:column; align-items:center; min-height:300px; justify-content:center; gap:16px }
.loading-box p { color:var(--text-soft); font-size:14px }
.empty-state { text-align:center; padding:60px 20px }
.empty-icon { font-size:56px; margin-bottom:16px }
.empty-state h3 { color:var(--text); font-size:18px; margin-bottom:8px }
.empty-state p { color:var(--text-soft); font-size:14px }

/* Dual Stack Layout */
.flash-layout { display:grid; grid-template-columns:200px 1fr 200px; gap:24px; margin-top:24px; align-items:start }

/* Side stacks */
.stack-column { }
.stack-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px }
.stack-label { font-size:13px; font-weight:700; color:var(--text-soft) }
.stack-count { font-size:13px; font-weight:800; color:var(--text); background:rgba(255,255,255,.06); padding:2px 10px; border-radius:20px }

.mini-stack { position:relative; min-height:80px }
.mini-card { position:relative; padding:10px 12px; border-radius:12px; background:var(--bg-card,#11243d); border:1px solid var(--border); margin-bottom:2px; transition:all .3s }
.mini-card p { margin:0; font-size:11px; color:var(--text-soft); line-height:1.4; white-space:nowrap; overflow:hidden; text-overflow:ellipsis }
.mini-more { text-align:center; font-size:11px; color:var(--text-soft); margin-top:8px; font-weight:600 }
.mc-hard { border-left:3px solid #ef4444 }
.mc-ok { border-left:3px solid #f59e0b }
.mc-easy { border-left:3px solid #10b981 }
.mc-emoji { position:absolute; top:8px; right:8px; font-size:12px }

/* Center */
.stack-center { min-width:0 }
.progress-row { display:flex; align-items:center; gap:12px; margin-bottom:16px }
.progress-track { flex:1; height:6px; background:rgba(255,255,255,.06); border-radius:10px; overflow:hidden }
.progress-fill { height:100%; background:linear-gradient(90deg,#3b82f6,#8b5cf6); border-radius:10px; transition:width .4s }
.progress-num { font-size:13px; font-weight:700; color:var(--text-soft); white-space:nowrap }

/* Flashcard */
.flashcard-wrap { perspective:1200px; cursor:pointer }
.flashcard { position:relative; width:100%; min-height:380px; transform-style:preserve-3d; transition:transform .6s cubic-bezier(.4,0,.2,1) }
.flashcard.flipped { transform:rotateY(180deg) }
.fc-face { position:absolute; top:0; left:0; right:0; min-height:380px; backface-visibility:hidden; border-radius:24px; padding:28px 24px; display:flex; flex-direction:column; border:1px solid var(--border); background:var(--bg-card,#11243d) }
.fc-back { transform:rotateY(180deg) }

.fc-badge { display:inline-flex; align-self:flex-start; padding:4px 14px; border-radius:20px; font-size:10px; font-weight:800; letter-spacing:1.5px; text-transform:uppercase; margin-bottom:16px }
.fc-badge-q { background:rgba(59,130,246,.12); color:#3b82f6 }
.fc-badge-a { background:rgba(16,185,129,.12); color:#10b981 }

.fc-img-wrap { margin-bottom:16px; border-radius:14px; overflow:hidden; max-height:180px }
.fc-img { width:100%; height:auto; max-height:180px; object-fit:contain }
.fc-text { font-size:17px; font-weight:600; color:var(--text); line-height:1.6; flex:1 }
.fc-hint { display:flex; align-items:center; justify-content:center; gap:6px; font-size:12px; color:var(--text-soft); margin-top:20px; opacity:.5 }

.fc-answer-box { display:flex; align-items:flex-start; gap:10px; padding:16px; background:rgba(16,185,129,.08); border-radius:14px; margin-bottom:16px }
.fc-check { font-size:22px; flex-shrink:0 }
.fc-answer-box p { font-size:16px; font-weight:600; color:#10b981; margin:0; line-height:1.5 }
.fc-explain { padding:14px; background:rgba(59,130,246,.06); border-radius:12px; border-left:3px solid #3b82f6 }
.fc-explain-title { font-size:12px; font-weight:700; color:#3b82f6; margin-bottom:6px }
.fc-explain p { font-size:13px; color:var(--text-soft); margin:0; line-height:1.6 }

/* Nav */
.fc-nav { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-top:16px }
.nav-btn { padding:10px 18px; border-radius:14px; border:1px solid var(--border); background:var(--bg-card); color:var(--text); font-size:13px; font-weight:600; cursor:pointer; transition:all .2s }
.nav-btn:hover:not(:disabled) { border-color:#3b82f6; color:#3b82f6 }
.nav-btn:disabled { opacity:.3; cursor:not-allowed }
.rate-btns { display:flex; gap:8px }
.rate-btn { width:44px; height:44px; border-radius:50%; border:2px solid var(--border); background:transparent; font-size:20px; cursor:pointer; transition:all .25s; display:flex; align-items:center; justify-content:center }
.rate-btn:hover { transform:scale(1.15) }
.r-hard:hover { border-color:#ef4444; background:rgba(239,68,68,.1) }
.r-ok:hover { border-color:#f59e0b; background:rgba(245,158,11,.1) }
.r-easy:hover { border-color:#10b981; background:rgba(16,185,129,.1) }

/* Rate Stats */
.rate-stats { display:flex; justify-content:center; gap:12px; margin-top:16px; padding:10px; background:var(--bg-card); border:1px solid var(--border); border-radius:12px }
.rs-item { display:flex; align-items:center; gap:4px; font-size:14px; font-weight:700 }
.rs-hard { color:#ef4444 } .rs-ok { color:#f59e0b } .rs-easy { color:#10b981 }

/* Responsive */
@media(max-width:1024px) {
  .flash-layout { grid-template-columns:1fr; gap:16px }
  .stack-left, .stack-right { display:none }
}
@media(max-width:768px) {
  .page-content { padding:0 14px 24px }
  .flash-toolbar { flex-direction:column; align-items:flex-start }
  .toolbar-right { width:100% }
  .topic-select { flex:1; min-width:0 }
  .flashcard, .fc-face { min-height:320px }
  .fc-text { font-size:15px }
  .fc-nav { flex-wrap:wrap; justify-content:center }
}
</style>
